import { useEffect, useState, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import type { MultiValue } from 'react-select';
import Button, { ButtonGroup, LoaderButton } from '../../../generic/Button';
import { FormGroup, FormLabel, FormLabelVariant, LabeledCheckbox, LabeledTextInput } from '../../../generic/Forms';
import { Select } from '../../../generic/Select';
import useSelectRoles from './hooks/useSelectRoles';
import { getErrorMessage } from '../../../../ReactQuery';
import { useInputValue } from '../../../../hooks';
import { useCreateTeam, useUpdateTeam } from '../../../../hooks/api';
import { useAddToast } from '../../../../contexts/ToastContext';
import { isFeatureEnabled, useIsFeatureEnabled } from '../../../../helpers/features';
import { TEAM_GROUPS, TEAM_UNIQUE_ID } from '../../../../../shared/featureFlags/flagNames';
import type { SelectOption } from '../../../../types';
import type { UpdateTeamRequest } from '../../../../../shared/types/routes/teams';
import TeamGroupSelector from './TeamGroupSelector';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  title: 'Create' | 'Edit';
  teamName: string;
  shouldAutoAddUsers: boolean;
  roleOptions: Array<SelectOption<number>>;
  selectedRole: SelectOption<number> | null;
  teamUniqueIdentifier?: string;
  isLoadingRoles: boolean;
  handleHideModal: () => void;
  handleSave: () => void;
  isSaving: boolean;
  isSaveButtonDisabled: boolean;
  onChangeTeamName: (teamName: string) => void;
  onChangeRole: (role: SelectOption<number> | null) => void;
  onChangeShouldAutoAddUsers: (shouldAutoAddUsers: boolean) => void;
  onChangeTeamUniqueIdentifier: (teamUniqueIdentifier: string) => void;
  isTeamGroupsEnabled: boolean;
  onTeamGroupSelect: (selectedTeamGroups: MultiValue<SelectOption<number>>) => void;
};

/**
 * Generic/"dumb" modal which is used for both creating and editing teams.
 * See the two wrapper components below which hook up state and logic.
 *
 * @param {Props} props - The props for the modal.
 * @param {boolean} props.isOpen - Whether the modal is open.
 * @param {() => void} props.onHide - The function to call when the modal is hidden.
 * @param {'Create' | 'Edit'} props.title - The title of the modal.
 * @param {string} props.teamName - The name of the team to edit.
 * @param {boolean} props.shouldAutoAddUsers - Whether to automatically add new users to the team.
 * @param {string} props.teamUniqueIdentifier - The unique identifier for the team.
 * @param {Array<SelectOption<number>>} props.roleOptions - The options for the role select.
 * @param {SelectOption<number> | null} props.selectedRole - The selected role for the team.
 * @param {boolean} props.isLoadingRoles - Whether the roles are loading.
 * @param {() => void} props.handleHideModal - The function to call when the modal is hidden.
 * @param {() => void} props.handleSave - The function to call when the team is created.
 * @param {boolean} props.isSaving - Whether the team is being created/saved.
 * @param {boolean} props.isSaveButtonDisabled - Whether the save button is disabled.
 * @param {(teamName: string) => void} props.onChangeTeamName - The function to call when the team name is changed.
 * @param {(role: SelectOption<number> | null) => void} props.onChangeRole - The function to call when the role is changed.
 * @param {(shouldAutoAddUsers: boolean) => void} props.onChangeShouldAutoAddUsers - The function to call when the should auto add users is changed.
 * @param {(teamUniqueIdentifier: string) => void} props.onChangeTeamUniqueIdentifier - The function to call when the team unique identifier is changed.
 */
const CreateEditTeamModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
  title,
  teamName,
  shouldAutoAddUsers,
  teamUniqueIdentifier,
  roleOptions,
  selectedRole,
  isLoadingRoles,
  handleHideModal,
  handleSave,
  isSaving,
  isSaveButtonDisabled,
  onChangeTeamName,
  onChangeRole,
  onChangeShouldAutoAddUsers,
  onChangeTeamUniqueIdentifier,
  isTeamGroupsEnabled,
  onTeamGroupSelect,
}) => {
  //  Note: this is another Ford-specific feature flag.
  const isTeamUniqueIdEnabled = useIsFeatureEnabled(TEAM_UNIQUE_ID);
  return (
    <Modal show={isOpen} onHide={handleHideModal} animation>
      <Modal.Header closeButton>
        <Modal.Title>
          {title} Team
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="margin-top-md">
        <FormGroup>
          <LabeledTextInput
            label="Team Name"
            value={teamName}
            onChange={onChangeTeamName}
            size="small"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a name for this team"
            autoFocus
          />
          {isTeamGroupsEnabled && (
            <TeamGroupSelector
              onTeamGroupSelect={onTeamGroupSelect}
            />
          )}
          <FormLabel label="Default Role" variant={FormLabelVariant.Filter}>
            <Select
              id="team-default-role-select"
              options={roleOptions}
              value={selectedRole}
              onChange={onChangeRole}
              isDisabled={isLoadingRoles}
            />
          </FormLabel>
        </FormGroup>
        <LabeledCheckbox
          label="Default team: Automatically add all new users to this team"
          isChecked={shouldAutoAddUsers}
          onChange={(_event, isChecked) => onChangeShouldAutoAddUsers(isChecked)}
        />
        {isTeamUniqueIdEnabled && (
          <div className="margin-top-sm">
            <LabeledTextInput
              label="Unique ID"
              value={teamUniqueIdentifier}
              onChange={onChangeTeamUniqueIdentifier}
              size="small"
              labelVariant={FormLabelVariant.Filter}
              placeholder="Enter a unique ID for this team (optional)"
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleSave}
            isDisabled={isSaveButtonDisabled}
            isLoading={isSaving}
          >
            {title} Team
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

type CreateTeamModalProps = Pick<Props, 'isOpen' | 'onHide'>;

/**
 * Container component for the create team modal.
 */
export const CreateTeamModal: VoidFunctionComponent<CreateTeamModalProps> = ({
  isOpen,
  onHide,
}) => {
  const isTeamGroupsEnabled = isFeatureEnabled(TEAM_GROUPS);
  const addToast = useAddToast();
  const [teamName, onChangeTeamName] = useInputValue();
  const {
    roleOptions,
    selectedRole,
    isLoading: isLoadingRoles,
    onChangeRole,
    resetSelectedRole,
  } = useSelectRoles();
  const [shouldAutoAddUsers, onChangeShouldAutoAddUsers] = useState(false);
  const [teamUniqueIdentifier, onChangeTeamUniqueIdentifier] = useInputValue();
  const isSaveButtonDisabled = !teamName?.trim() || !selectedRole;

  const [selectedTeamGroups, setSelectedTeamGroups] = useState<Set<number>>(new Set());
  const onTeamGroupSelect = (nextSelectedteamGroups: MultiValue<SelectOption<number>>) =>
    setSelectedTeamGroups(new Set(nextSelectedteamGroups.map(teamGroup => teamGroup.value)));

  const {
    mutateAsync: createTeam,
    isLoading: isLoadingCreateTeam,
    reset: resetCreateTeamError,
  } = useCreateTeam({ // We pass the new team data here.
    name: teamName?.trim(),
    defaultRoleId: selectedRole?.value ?? 0,
    autoAddUsers: shouldAutoAddUsers,
    //  This is explicit, so that we don't send an empty string.
    uniqueIdentifier: teamUniqueIdentifier?.trim() || null,
    teamGroupIds: selectedTeamGroups.size > 0 ? Array.from(selectedTeamGroups) : null,
  });

  //  Lots of things need to happen when we hide the modal.
  const handleHideModal = () => {
    onHide();
    onChangeTeamName('');
    resetSelectedRole();
    onChangeTeamUniqueIdentifier('');
    onChangeShouldAutoAddUsers(false);
    setSelectedTeamGroups(new Set());
    resetCreateTeamError();
  };

  //  Create the team, but display a toast if it doesn't work.
  const handleCreateTeam = async () => {
    try {
      await createTeam();
      handleHideModal();
      addToast({
        type: 'success',
        content: `Team "${teamName}" created successfully`,
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'An error occurred while creating the team.'),
      });
    }
  };

  return (
    <CreateEditTeamModal
      isOpen={isOpen}
      onHide={onHide}
      title="Create"
      teamName={teamName}
      roleOptions={roleOptions}
      selectedRole={selectedRole}
      isLoadingRoles={isLoadingRoles}
      shouldAutoAddUsers={shouldAutoAddUsers}
      teamUniqueIdentifier={teamUniqueIdentifier}
      handleHideModal={handleHideModal}
      handleSave={handleCreateTeam}
      isSaving={isLoadingCreateTeam}
      isSaveButtonDisabled={isSaveButtonDisabled}
      onChangeTeamName={onChangeTeamName}
      onChangeRole={onChangeRole}
      onChangeShouldAutoAddUsers={onChangeShouldAutoAddUsers}
      onChangeTeamUniqueIdentifier={onChangeTeamUniqueIdentifier}
      isTeamGroupsEnabled={isTeamGroupsEnabled}
      onTeamGroupSelect={onTeamGroupSelect}
    />
  );
};

type EditTeamModalProps = Pick<Props, 'isOpen' | 'onHide'> & {
  team: UpdateTeamRequest | null;
};

/**
 * Container component for the edit team modal.
 */
export const EditTeamModal: VoidFunctionComponent<EditTeamModalProps> = ({
  isOpen,
  onHide,
  team,
}) => {
  const addToast = useAddToast();
  const [teamName, onChangeTeamName] = useInputValue(team?.name);
  const {
    roleOptions,
    selectedRole,
    isLoading: isLoadingRoles,
    onChangeRole,
    resetSelectedRole,
  } = useSelectRoles(team?.defaultRoleId);
  const [shouldAutoAddUsers, onChangeShouldAutoAddUsers] = useState(team?.autoAddUsers ?? false);
  const [teamUniqueIdentifier, onChangeTeamUniqueIdentifier] = useInputValue(team?.uniqueIdentifier ?? '');
  const isSaveButtonDisabled = !teamName || !selectedRole;

  //  Update the team name when the props change.
  useEffect(() => {
    if (team?.name) {
      onChangeTeamName(team.name);
    }
  }, [team?.name, onChangeTeamName]);

  //  Update the unique identifier when the props change.
  useEffect(() => {
    if (team?.uniqueIdentifier) {
      onChangeTeamUniqueIdentifier(team.uniqueIdentifier);
    }
  }, [team?.uniqueIdentifier, onChangeTeamUniqueIdentifier]);

  const {
    mutateAsync: updateTeam,
    isLoading: isLoadingUpdateTeam,
    reset: resetUpdateTeamError,
  } = useUpdateTeam({ // We pass the new team data here.
    id: team?.id ?? 0,
    name: teamName.trim(),
    defaultRoleId: selectedRole?.value ?? 0,
    autoAddUsers: shouldAutoAddUsers ?? false,
    //  This is explicit, so that we don't send an empty string.
    uniqueIdentifier: teamUniqueIdentifier.trim() || null,
  });

  //  Clear the state when we hide the modal.
  const handleHideModal = () => {
    onHide();
    onChangeTeamName('');
    resetSelectedRole();
    onChangeTeamUniqueIdentifier('');
    onChangeShouldAutoAddUsers(false);
    resetUpdateTeamError();
  };

  //  Update the team, but display a toast if it doesn't work.
  const handleUpdateTeam = async () => {
    try {
      await updateTeam();
      addToast({
        type: 'success',
        content: `Team "${teamName}" updated successfully`,
      });
      handleHideModal();
    } catch (error: unknown) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'An error occurred while updating the team.'),
      });
    }
  };

  return (
    <CreateEditTeamModal
      isOpen={isOpen}
      onHide={onHide}
      title="Edit"
      teamName={teamName}
      roleOptions={roleOptions}
      selectedRole={selectedRole}
      isLoadingRoles={isLoadingRoles}
      shouldAutoAddUsers={shouldAutoAddUsers}
      teamUniqueIdentifier={teamUniqueIdentifier}
      handleHideModal={handleHideModal}
      handleSave={handleUpdateTeam}
      isSaving={isLoadingUpdateTeam}
      isSaveButtonDisabled={isSaveButtonDisabled}
      onChangeTeamName={onChangeTeamName}
      onChangeRole={onChangeRole}
      onChangeShouldAutoAddUsers={onChangeShouldAutoAddUsers}
      onChangeTeamUniqueIdentifier={onChangeTeamUniqueIdentifier}
    />
  );
};
